/**
 * Module: screens/creator/steps/SelfieStep
 * Role: Wizard Step 2/8 — selfie capture (FTR-1106 + FTR-1131 chrome rewrite
 *       2026-06-07 round 3). Founder report 2026-06-07: «зачем-то, блядь,
 *       синяя кнопка внизу. Почему она такая? На черном фоне. Синяя кнопка
 *       вообще не в тему». That was Telegram's native MainButton — driven
 *       by the legacy `canUpload=false → setMainButton(...)` branch which we
 *       inherited from when TG WebApp didn't expose a file picker reliably.
 *
 *       Modern TG WebApp (BotAPI 7+) handles `<input type="file" accept="image/*">`
 *       natively, so we always render the in-app WizardCtaBar — pixel-matched
 *       to the design system, no chrome-blue surprise. If a very old TG client
 *       can't open the picker, the user can still proceed via the picker
 *       opening on tap of the dashed dropzone (and we'll surface that as a
 *       separate fix if it actually breaks in the wild).
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/store:useWizardStore
 *   - lib/imageResize:resizeImage, ImageResizeError
 *   - screens/creator/_chrome/WizardShell
 *   - screens/flow/atoms/HintBar
 *   - styles/tokens:P, T
 *
 * Used by: CreatorOnboardingScreen.tsx
 * Glossary: ai/glossary/buyer.md
 *
 * ADR-023: no window.Telegram access, no platform literals
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardStore } from '../store';
import { resizeImage, ImageResizeError } from '../../../lib/imageResize';
import { HintBar } from '../../flow/atoms/HintBar';
import { P, T } from '../../../styles/tokens';
import { Ic } from '../../../components/atoms/Ic';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

export default function SelfieStep() {
  const navigate = useNavigate();
  const { draft, setSelfie } = useWizardStore();

  const [preview, setPreview] = useState<string>(draft.selfie_dataUrl || '');
  const [hint, setHint] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasPreview = !!preview;
  const openPicker = () => fileInputRef.current?.click();

  const handleBack = () => navigate(-1);
  const goNext = () => navigate('/creator-onboarding/consent');
  const handlePrimary = () => (hasPreview ? goNext() : openPicker());

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;

    setHint(null);
    setProcessing(true);
    try {
      const result = await resizeImage(file);
      setPreview(result.dataUrl);
      setSelfie(result.dataUrl);
    } catch (err) {
      if (err instanceof ImageResizeError) setHint(err.message);
      else setHint('Не удалось обработать фото. Попробуйте ещё раз.');
    } finally {
      setProcessing(false);
    }
  }

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={2} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Селфи
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Сделайте чёткое фото лица — оно нужно для верификации личности
          </div>
        </div>

        <button
          type="button"
          onClick={openPicker}
          disabled={processing}
          aria-label={hasPreview ? 'Переснять селфи' : 'Сделать селфи'}
          style={{
            width: '100%',
            aspectRatio: '1',
            borderRadius: 16,
            border: `2px dashed ${hasPreview ? P.success : P.line}`,
            background: hasPreview ? 'transparent' : 'rgba(60,50,35,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            padding: 0,
            cursor: processing ? 'default' : 'pointer',
          }}
        >
          {hasPreview ? (
            <img
              src={preview}
              alt="Превью селфи"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: 0.4 }}>
              {processing
                ? <span style={{ font: T.footnote, color: P.inkSoft }}>…</span>
                : (
                  <>
                    <Ic name="camera" size={38} color={P.inkSoft} />
                    <span style={{ font: T.footnote, color: P.inkSoft }}>
                      Нажмите, чтобы сделать селфи
                    </span>
                  </>
                )}
            </span>
          )}
        </button>

        {hasPreview && (
          <div style={{ font: T.captionAlt, color: P.inkSoft, textAlign: 'center' }}>
            Нажмите на фото, чтобы переснять
          </div>
        )}

        {hint && <HintBar>{hint}</HintBar>}
      </WizardBody>

      {/* Hidden file input — always mounted. No `capture` so the native sheet
          offers both camera and gallery in modern TG/VK WebApps. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-hidden="true"
      />

      <WizardCtaBar
        label={hasPreview ? 'Далее' : 'Сделать селфи'}
        onClick={handlePrimary}
        disabled={processing}
      />
    </WizardFrame>
  );
}
